export const HANDOFF_TOKEN_KEY = 'bamboo.handoff.token';
export const HANDOFF_IMITATING_KEY = 'bamboo.handoff.imitating';
export const HANDOFF_EVENT_KEY = 'bamboo.handoff.eventLogged';
export const HANDOFF_TAB_KEY = 'bamboo.handoff.tab';
export const HANDOFF_CHECK_MS = 60 * 1000;
const CODE_PARAM = 'code';

const apiUrl = () => import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export type HandoffBootstrap =
  | { status: 'none' }
  | { status: 'signed-in'; imitating: string | null }
  | { status: 'failed'; reason: string };

type Payload = { sub?: unknown; exp?: unknown };

function decodePayload(token: string): Payload | null {
  const part = token.split('.')[1];
  if (!part) return null;
  try {
    const base64 = part.replaceAll('-', '+').replaceAll('_', '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const parsed: unknown = JSON.parse(window.atob(padded));
    return parsed && typeof parsed === 'object' ? (parsed as Payload) : null;
  } catch {
    return null;
  }
}

function unexpired(payload: Payload | null): boolean {
  return typeof payload?.exp === 'number' && payload.exp * 1000 > Date.now();
}

export function preauthCodeFromUrl(url: URL = new URL(window.location.href)): string | null {
  const code = url.searchParams.get(CODE_PARAM)?.trim();
  return code || null;
}

export function stripPreauthCode(url: URL): void {
  url.searchParams.delete(CODE_PARAM);
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
}

function read(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function remove(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    return;
  }
}

export function clearHandoff(): void {
  remove(HANDOFF_TOKEN_KEY);
  remove(HANDOFF_IMITATING_KEY);
  remove(HANDOFF_EVENT_KEY);
}

// The marker outlives clearHandoff: a refused handoff clears the token first
// and abandons the imitation after, and that teardown must still find the
// imitation in this tab's storage rather than remove another tab's shared one.
export function isHandoffTab(): boolean {
  return read(HANDOFF_TAB_KEY) !== null;
}

export function forgetHandoffTab(): void {
  remove(HANDOFF_TAB_KEY);
}

// A handoff tab keeps its imitation and district settings to itself: shared
// keys would let two Reports tabs opened under different FL360 imitations end
// each other or read each other's district.
export function tabScopedStorage(): Storage {
  return isHandoffTab() ? sessionStorage : localStorage;
}

export function getHandoffToken(): string | null {
  const token = read(HANDOFF_TOKEN_KEY);
  if (!token) return null;
  if (unexpired(decodePayload(token))) return token;
  clearHandoff();
  return null;
}

export function handoffSubject(): string | null {
  const token = getHandoffToken();
  const sub = token ? decodePayload(token)?.sub : null;
  return typeof sub === 'string' && sub ? sub : null;
}

export function handoffImitating(): string | null {
  return getHandoffToken() ? read(HANDOFF_IMITATING_KEY) : null;
}

type ExchangeReply = { token?: unknown; impersonated_email?: unknown; error?: unknown };

const EXCHANGE_RETRY_DELAY_MS = 500;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const newNonce = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;

const transient = (response: Response | null): boolean => response === null || response.status >= 500;

// The same nonce on the retry lets the server answer again if the first
// reply was lost after the code had been marked used.
async function postExchange(code: string, retryDelayMs: number): Promise<Response | null> {
  const body = JSON.stringify({ code, nonce: newNonce() });
  const attempt = async (): Promise<Response | null> => {
    try {
      return await fetch(`${apiUrl()}/preauth/exchange`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      });
    } catch {
      return null;
    }
  };
  const first = await attempt();
  if (!transient(first)) return first;
  await wait(retryDelayMs);
  return attempt();
}

async function exchange(code: string, retryDelayMs: number): Promise<HandoffBootstrap> {
  const response = await postExchange(code, retryDelayMs);
  if (!response) return { status: 'failed', reason: 'unreachable' };
  let reply: ExchangeReply = {};
  try {
    reply = ((await response.json()) as ExchangeReply | null) ?? {};
  } catch {
    reply = {};
  }
  if (!response.ok) {
    return { status: 'failed', reason: typeof reply.error === 'string' ? reply.error : `http_${response.status}` };
  }
  const token = typeof reply.token === 'string' ? reply.token.trim() : '';
  if (!token) return { status: 'failed', reason: 'no_token' };
  if (!unexpired(decodePayload(token))) return { status: 'failed', reason: 'expired' };
  const imitating = typeof reply.impersonated_email === 'string' && reply.impersonated_email ? reply.impersonated_email : null;
  try {
    sessionStorage.setItem(HANDOFF_TOKEN_KEY, token);
    if (imitating) sessionStorage.setItem(HANDOFF_IMITATING_KEY, imitating);
    sessionStorage.setItem(HANDOFF_TAB_KEY, '1');
  } catch {
    clearHandoff();
    return { status: 'failed', reason: 'storage' };
  }
  return { status: 'signed-in', imitating };
}

const inFlight = new Map<string, Promise<HandoffBootstrap>>();
let latest: Promise<HandoffBootstrap> | null = null;

export type BootstrapOptions = { retryDelayMs?: number };

// The code leaves the address as soon as the exchange starts, so a caller
// that arrives during the exchange (StrictMode replays the bootstrap effect)
// is handed the exchange already running instead of "none".
export function bootstrapHandoffFromUrl(
  href: string = window.location.href,
  { retryDelayMs = EXCHANGE_RETRY_DELAY_MS }: BootstrapOptions = {}
): Promise<HandoffBootstrap> {
  const url = new URL(href);
  const code = preauthCodeFromUrl(url);
  if (!code) return latest ?? Promise.resolve({ status: 'none' });
  const pending = inFlight.get(code);
  if (pending) return pending;
  stripPreauthCode(url);
  clearHandoff();
  const run = exchange(code, retryDelayMs).finally(() => {
    inFlight.delete(code);
    if (latest === run) latest = null;
  });
  inFlight.set(code, run);
  latest = run;
  return run;
}
